import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Extract elements before the timeout
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    const newVariantId = event.detail.resource?.id ?? '';

    // Delay the DOM manipulation to prevent conflicts with third-party app scripts
    setTimeout(() => {
      const { priceContainer, volumePricingNote } = this.refs;

      // Update price container
      if (newPrice && priceContainer) {
        priceContainer.innerHTML = newPrice.innerHTML;
        priceContainer.className = newPrice.className;
      }

      // Update volume pricing note
      if (!newNote) {
        volumePricingNote?.remove();
      } else if (!volumePricingNote) {
        priceContainer?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
      } else {
        volumePricingNote.innerHTML = newNote.innerHTML;
        volumePricingNote.className = newNote.className;
      }

      // Update installments (SPI banner) variant ID to trigger payment terms re-render
      const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
      const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
      if (installmentsInput) {
        installmentsInput.value = newVariantId;
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 800); 
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}